import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import api from '../services/api'
import WellnessScoreQuestion from './WellnessScoreQuestion'
import { WELLNESS_CHECK_IN_SCORES } from '../constants/wellnessCheckInScores'
import { getUserToken } from '../utils/userData'
import { API_ENDPOINTS } from '../utils/constants'

const CohortCheckInModal = ({
  isOpen,
  onClose,
  sessionNumber,
  checkInType,
  onSuccess,
  isAuthenticated = true,
  onUnauthenticatedComplete,
  programSlug = 'build3-cohort-7',
}) => {
  const [step, setStep] = useState('welcome') // 'welcome', 'rating', or 'feedback'
  const [formData, setFormData] = useState({
    energyLevel: null,
    stressLevel: null,
    emotionalState: null,
    physicalState: null,
    heartRate: '',
  })
  const [feedbackData, setFeedbackData] = useState({
    enjoymentRating: null,
    techniqueUsageRating: null,
    facilitatorRating: null,
    additionalFeedback: '',
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [checkInSubmitted, setCheckInSubmitted] = useState(false)

  const isPostEvent = checkInType === 'post-event'

  // Reset step when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('welcome')
      setCheckInSubmitted(false)
    }
  }, [isOpen])

  const feedbackQuestions = [
    { key: 'enjoymentRating', label: 'How did you enjoy today\'s session?', emoji: '🌟', lowLabel: 'Not so much', highLabel: 'Very much' },
    { key: 'techniqueUsageRating', label: 'How likely are you to use the techniques of this session going ahead?', emoji: '🎯', lowLabel: 'Unlikely', highLabel: 'Very likely' },
    { key: 'facilitatorRating', label: 'How effective was the facilitator in delivering today\'s session?', emoji: '👩‍🏫', lowLabel: 'Not very effective', highLabel: 'Very effective' },
  ]

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }))
    // Clear error when user starts typing
    if (errors[key] || errors.general) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[key]
        delete newErrors.general
        return newErrors
      })
    }
  }

  const handleFeedbackChange = (key, value) => {
    setFeedbackData((prev) => ({
      ...prev,
      [key]: value,
    }))
    // Clear error when user starts typing
    if (errors[key] || errors.general) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[key]
        delete newErrors.general
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}
    for (const { key, label } of WELLNESS_CHECK_IN_SCORES) {
      const value = formData[key]
      if (value === null || value === undefined) {
        newErrors[key] = `Please select your ${label}`
        return newErrors
      }
      if (value < 1 || value > 10) {
        newErrors[key] = `${label} must be between 1 and 10`
        return newErrors
      }
    }
    return null
  }

  const validateFeedback = () => {
    const newErrors = {}
    for (const { key } of feedbackQuestions) {
      const value = feedbackData[key]
      if (value === null || value === undefined) {
        newErrors[key] = `Please select a rating`
        return newErrors
      }
      if (value < 1 || value > 10) {
        newErrors[key] = `Rating must be between 1 and 10`
        return newErrors
      }
    }
    return null
  }

  const handleSubmitCheckIn = async (e) => {
    e.preventDefault()
    setErrors({})

    const validationErrors = validateForm()
    if (validationErrors) {
      setErrors(validationErrors)
      return
    }

    // If user is not authenticated, call the unauthenticated callback
    if (!isAuthenticated) {
      setCheckInSubmitted(true)
      
      // For post-event, go to feedback step with local data; otherwise complete
      if (isPostEvent) {
        setStep('feedback')
      } else {
      onUnauthenticatedComplete && onUnauthenticatedComplete({ ...formData })
      // Reset form
      setFormData({
        energyLevel: null,
        stressLevel: null,
        emotionalState: null,
        physicalState: null,
        heartRate: '',
      })
      setStep('welcome')
    }
    return
  }

    setIsSubmitting(true)

    try {
      const token = getUserToken()
      const response = await api.post(
        API_ENDPOINTS.PROGRAMS.CHECK_INS(programSlug),
        {
          sessionNumber,
          checkInType,
          ...formData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data.success) {
        setCheckInSubmitted(true)
        
        // For post-event, go to feedback step; otherwise complete
        if (isPostEvent) {
          setStep('feedback')
        } else {
          onSuccess && onSuccess(response.data.data)
          onClose()
          // Reset form
          setFormData({
            energyLevel: null,
            stressLevel: null,
            emotionalState: null,
            physicalState: null,
            heartRate: '',
          })
          setStep('welcome')
        }
      }
    } catch (error) {
      console.error('Error submitting check-in:', error)
      setErrors({
        general: error.response?.data?.message || 'Failed to submit check-in. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmitFeedback = async (e) => {
    e.preventDefault()
    setErrors({})

    const validationErrors = validateFeedback()
    if (validationErrors) {
      setErrors(validationErrors)
      return
    }

    // If user is not authenticated, call the unauthenticated callback with both check-in and feedback data
    if (!isAuthenticated) {
      onUnauthenticatedComplete && onUnauthenticatedComplete({ 
        ...formData, 
        feedback: { ...feedbackData }
      })
      // Reset form
      setFormData({
        energyLevel: null,
        stressLevel: null,
        emotionalState: null,
        physicalState: null,
        heartRate: '',
      })
      setFeedbackData({
        enjoymentRating: null,
        techniqueUsageRating: null,
        facilitatorRating: null,
        additionalFeedback: '',
      })
      setStep('welcome')
      setCheckInSubmitted(false)
      return
    }

    setIsSubmitting(true)

    try {
      const token = getUserToken()
      const response = await api.post(
        API_ENDPOINTS.PROGRAMS.FEEDBACK(programSlug),
        {
          sessionNumber,
          ...feedbackData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data.success) {
        onSuccess && onSuccess({ checkIn: checkInSubmitted, feedback: response.data.data })
        onClose()
        // Reset form
        setFormData({
          energyLevel: null,
          stressLevel: null,
          emotionalState: null,
          physicalState: null,
          heartRate: '',
        })
        setFeedbackData({
          enjoymentRating: null,
          techniqueUsageRating: null,
          facilitatorRating: null,
          additionalFeedback: '',
        })
        setStep('welcome')
        setCheckInSubmitted(false)
      }
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setErrors({
        general: error.response?.data?.message || 'Failed to submit feedback. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSkipFeedback = () => {
    // If user is not authenticated, call the unauthenticated callback with check-in data only
    if (!isAuthenticated) {
      onUnauthenticatedComplete && onUnauthenticatedComplete({ ...formData })
    } else {
      onSuccess && onSuccess({ checkIn: checkInSubmitted })
      onClose()
    }
    // Reset form
    setFormData({
      energyLevel: null,
      stressLevel: null,
      emotionalState: null,
      physicalState: null,
      heartRate: '',
    })
    setFeedbackData({
      enjoymentRating: null,
      techniqueUsageRating: null,
      facilitatorRating: null,
      additionalFeedback: '',
    })
    setStep('welcome')
    setCheckInSubmitted(false)
  }

  const checkInTypeLabel = checkInType === 'pre-event' ? 'Pre-Event' : 'Post-Event'
  const isPreEvent = checkInType === 'pre-event'

  // Welcome screen content based on check-in type - memoized to prevent recreation
  const welcomeContent = useMemo(() => isPreEvent
    ? {
        emoji: '🌟',
        title: "You're Here!",
        subtitle: `Session ${sessionNumber}`,
        message:
          "Amazing! You've taken a wonderful step by showing up today. Your commitment to your wellness journey is truly inspiring.",
        subMessage:
          "Before we begin, let's capture how you're feeling right now. This helps us understand your journey better.",
        buttonText: "Let's Begin",
        gradient: 'from-emerald-400 via-teal-500 to-cyan-500',
        bgGradient: 'from-emerald-50 to-teal-50',
        particleColors: ['#10b981', '#14b8a6', '#06b6d4', '#22c55e'],
      }
    : {
        emoji: '🎉',
        title: 'Thank You!',
        subtitle: `Session ${sessionNumber} Complete`,
        message:
          "Congratulations on completing today's session! You've invested in yourself, and that's something to celebrate.",
        subMessage:
          "Let's capture how you're feeling now so we can see the positive impact of your wellness practice.",
        buttonText: 'Share My Experience',
        gradient: 'from-violet-400 via-purple-500 to-fuchsia-500',
        bgGradient: 'from-violet-50 to-purple-50',
        particleColors: ['#8b5cf6', '#a855f7', '#d946ef', '#c084fc'],
      }, [isPreEvent, sessionNumber])

  // Memoize particle positions so they don't regenerate on every render
  const particles = useMemo(() => 
    [...Array(20)].map((_, i) => ({
      id: i,
      width: Math.random() * 12 + 6,
      height: Math.random() * 12 + 6,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      colorIndex: Math.floor(Math.random() * 4),
      xOffset: Math.random() * 20 - 10,
      duration: 3 + Math.random() * 2,
      delay: Math.random() * 2,
    })), []
  )

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden"
            >
              <AnimatePresence mode="wait">
                {step === 'welcome' ? (
                  /* Welcome/Thank you screen */
                  <motion.div
                    key="welcome"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, x: -50 }}
                    className={`relative min-h-[500px] flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br ${welcomeContent.bgGradient} rounded-3xl overflow-hidden`}
                  >
                    {/* Floating particles */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      {particles.map((particle) => (
                        <motion.div
                          key={particle.id}
                          className="absolute rounded-full"
                          style={{
                            width: particle.width,
                            height: particle.height,
                            background: welcomeContent.particleColors[particle.colorIndex],
                            left: particle.left,
                            top: particle.top,
                            opacity: 0.3,
                          }}
                          animate={{
                            y: [0, -30, 0],
                            x: [0, particle.xOffset, 0],
                            scale: [1, 1.2, 1],
                            opacity: [0.3, 0.6, 0.3],
                          }}
                          transition={{
                            duration: particle.duration,
                            repeat: Infinity,
                            delay: particle.delay,
                            ease: 'easeInOut',
                          }}
                        />
                      ))}
                    </div>

                    {/* Animated emoji */}
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        delay: 0.2,
                      }}
                      className="relative z-10"
                    >
                      <motion.span
                        className="text-8xl block"
                        animate={{
                          y: [0, -10, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        {welcomeContent.emoji}
                      </motion.span>
                    </motion.div>

                    {/* Glowing ring behind emoji */}
                    <motion.div
                      className={`absolute w-32 h-32 rounded-full bg-gradient-to-r ${welcomeContent.gradient} blur-xl`}
                      style={{ top: '50%', left: '50%', transform: 'translate(-50%, -80%)' }}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />

                    {/* Title with stagger animation */}
                    <motion.h2
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                      className={`text-4xl md:text-5xl font-bold mt-6 bg-gradient-to-r ${welcomeContent.gradient} bg-clip-text text-transparent relative z-10`}
                    >
                      {welcomeContent.title}
                    </motion.h2>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                      className="text-gray-500 font-medium mt-2 relative z-10"
                    >
                      {welcomeContent.subtitle}
                    </motion.p>

                    {/* Message */}
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                      className="text-gray-700 text-lg mt-6 max-w-md leading-relaxed relative z-10"
                    >
                      {welcomeContent.message}
                    </motion.p>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7, duration: 0.6 }}
                      className="text-gray-500 text-sm mt-4 max-w-sm relative z-10"
                    >
                      {welcomeContent.subMessage}
                    </motion.p>

                    {/* CTA Button */}
                    <motion.button
                      initial={{ opacity: 0, y: 30, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0.9, duration: 0.5, type: 'spring' }}
                      whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setStep('rating')}
                      className={`mt-8 px-10 py-4 bg-gradient-to-r ${welcomeContent.gradient} text-white font-bold text-lg rounded-2xl shadow-lg relative z-10 overflow-hidden group`}
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        {welcomeContent.buttonText}
                        <motion.span
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1, repeat: Infinity }}
                        >
                          →
                        </motion.span>
                      </span>
                      <motion.div
                        className="absolute inset-0 bg-white"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '100%' }}
                        transition={{ duration: 0.5 }}
                        style={{ opacity: 0.2 }}
                      />
                    </motion.button>

                    {/* Decorative shapes */}
                    <motion.div
                      className={`absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br ${welcomeContent.gradient} opacity-10`}
                      animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 90, 0],
                      }}
                      transition={{ duration: 10, repeat: Infinity }}
                    />
                    <motion.div
                      className={`absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-gradient-to-br ${welcomeContent.gradient} opacity-10`}
                      animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, -90, 0],
                      }}
                      transition={{ duration: 8, repeat: Infinity }}
                    />
                  </motion.div>
                ) : step === 'rating' ? (
                  /* Rating screen */
                  <motion.div
                    key="rating"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    className="p-8"
                  >
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => setStep('welcome')}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <span className="text-sm text-gray-500">Back</span>
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        {checkInTypeLabel} Check-In
                      </h2>
                      <p className="text-gray-600">
                        Session {sessionNumber} - Rate your current wellness levels
                      </p>
                      {isPostEvent && (
                        <p className="text-sm text-purple-600 mt-1">Step 1 of 2</p>
                      )}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmitCheckIn} className="space-y-6">
                      <div className="space-y-8">
                        {WELLNESS_CHECK_IN_SCORES.map((score) => (
                          <WellnessScoreQuestion
                            key={score.key}
                            score={score}
                            value={formData[score.key]}
                            onChange={(rating) => handleInputChange(score.key, rating)}
                            error={errors[score.key]}
                            variant="primary"
                          />
                        ))}

                        {/* Heart Rate - Optional */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ❤️ Heart Rate (BPM) <span className="text-gray-400 font-normal">- Optional</span>
                          </label>
                          <p className="text-xs text-gray-500 mb-3">
                            If you have a way to measure your heart rate (fitness tracker, pulse check), enter it here
                          </p>
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              value={formData.heartRate}
                              onChange={(e) => handleInputChange('heartRate', e.target.value)}
                              placeholder="e.g., 72"
                              min="30"
                              max="220"
                              className="w-32 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all text-center text-lg font-medium"
                            />
                            <span className="text-gray-500 text-sm">beats per minute</span>
                          </div>
                          {errors.heartRate && <p className="text-red-600 text-sm mt-1">{errors.heartRate}</p>}
                        </div>
                      </div>

                      {errors.general && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-600 text-sm">{errors.general}</p>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="flex gap-4 pt-4">
                        <button
                          type="button"
                          onClick={onClose}
                          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : isPostEvent ? 'Next: Session Feedback' : 'Submit Check-In'}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                ) : (
                  /* Feedback screen (post-event only) */
                  <motion.div
                    key="feedback"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    className="p-8"
                  >
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => setStep('rating')}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <span className="text-sm text-gray-500">Back</span>
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        Session Feedback
                      </h2>
                      <p className="text-gray-600">
                        Session {sessionNumber} - Tell us about your experience
                      </p>
                      <p className="text-sm text-purple-600 mt-1">Step 2 of 2</p>
                    </div>

                    {/* Success indicator for check-in */}
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-green-700">Wellness check-in saved!</span>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmitFeedback} className="space-y-6">
                      <div className="space-y-8">
                        {feedbackQuestions.map(({ key, label, emoji, lowLabel, highLabel }) => (
                          <div key={key}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {emoji} {label}
                            </label>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">1 - {lowLabel}</span>
                              <span className="text-xs text-gray-500">10 - {highLabel}</span>
                            </div>
                            <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                                <label
                                  key={rating}
                                  className={`relative flex items-center justify-center cursor-pointer touch-manipulation aspect-square rounded-lg border-2 transition-all min-h-[44px] ${
                                    feedbackData[key] === rating
                                      ? 'bg-purple-600 border-purple-600 text-white shadow-md'
                                      : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400 hover:bg-purple-50'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={key}
                                    value={rating}
                                    checked={feedbackData[key] === rating}
                                    onChange={(e) => handleFeedbackChange(key, parseInt(e.target.value))}
                                    className="sr-only"
                                  />
                                  <span className="text-sm font-medium">{rating}</span>
                                </label>
                              ))}
                            </div>
                            {errors[key] && <p className="text-red-600 text-sm mt-1">{errors[key]}</p>}
                          </div>
                        ))}

                        {/* Text feedback */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            📝 Please share any feedback for the session or anything that would make the next session more effective for you
                          </label>
                          <textarea
                            value={feedbackData.additionalFeedback}
                            onChange={(e) => handleFeedbackChange('additionalFeedback', e.target.value)}
                            placeholder="Your thoughts and suggestions... (optional)"
                            rows={4}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
                          />
                        </div>
                      </div>

                      {errors.general && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-600 text-sm">{errors.general}</p>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="flex gap-4 pt-4">
                        <button
                          type="button"
                          onClick={handleSkipFeedback}
                          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                          disabled={isSubmitting}
                        >
                          Skip
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CohortCheckInModal
